/**
 * Store Profile Page - Todo en Local Marketplace
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Store, Truck, Package, Star, Shield, Clock,
  Phone, Mail, ChevronRight, Heart, ChevronDown, ChevronUp,
  Search, ShoppingCart, Share2, Copy, Check, ExternalLink, Plus, MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { getProductImage } from '../../utils/constants';
import { getErrorMessage } from '../../utils/errorUtils';
import api from '../../services/api';
import VerifiedBadge from '../../components/marketplace/VerifiedBadge';
import { MarketplaceHeader, MarketplaceFooter, FloatingCartButton, MobileBottomNav } from '../../components/marketplace/MarketplaceLayout';

// Brand colors
const COLORS = {
  navyBlue: '#1e3a5f',
  darkSlate: '#0f172a',
  orange: '#F37322',
  lightGray: '#f8fafc',
  white: '#ffffff'
};

// Day names mapping
const DAY_NAMES = {
  monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles',
  thursday: 'Jueves', friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo'
};

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const getCurrentDayKey = () => {
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return dayNames[new Date().getDay()];
};

// Helper function to get branch open status
const getBranchOpenStatus = (branch) => {
  if (branch.is_manually_closed) {
    return { is_open: false, message: 'Cerrado temporalmente', reason: 'manual' };
  }
  if (!branch.is_active) {
    return { is_open: false, message: 'Sucursal inactiva', reason: 'inactive' };
  }

  const now = new Date();
  const currentDay = getCurrentDayKey();
  const currentTime = now.toTimeString().slice(0, 5);

  const hours = branch.business_hours;
  if (!hours) {
    return { is_open: true, message: 'Abierto', reason: 'no_hours' };
  }

  const daySchedule = hours[currentDay];
  if (!daySchedule || !daySchedule.is_open) {
    return { is_open: false, message: 'Cerrado hoy', reason: 'day_closed' };
  }

  const openTime = daySchedule.open_time || '09:00';
  const closeTime = daySchedule.close_time || '18:00';

  if (currentTime < openTime) {
    return { is_open: false, message: `Abre a las ${openTime}`, reason: 'not_yet' };
  }
  if (currentTime >= closeTime) {
    return { is_open: false, message: 'Cerrado · Abre mañana', reason: 'closed' };
  }

  const [closeH, closeM] = closeTime.split(':').map(Number);
  const [currH, currM] = currentTime.split(':').map(Number);
  const minsUntilClose = (closeH * 60 + closeM) - (currH * 60 + currM);

  if (minsUntilClose <= 30) {
    return { is_open: true, message: `Cierra pronto · ${minsUntilClose} min`, reason: 'closing_soon' };
  }

  return { is_open: true, message: `Abierto · Cierra a las ${closeTime}`, reason: 'open' };
};

// Helper to get hours summary
const getHoursSummary = (hours) => {
  if (!hours) return 'Horario no configurado';

  const openDays = DAY_ORDER.filter(d => hours[d]?.is_open);
  if (openDays.length === 0) return 'Cerrado todos los días';
  if (openDays.length === 7) {
    const sameHours = openDays.every(d =>
      hours[d]?.open_time === hours.monday?.open_time &&
      hours[d]?.close_time === hours.monday?.close_time
    );
    if (sameHours) {
      return `Todos los días ${hours.monday?.open_time} - ${hours.monday?.close_time}`;
    }
  }

  const firstOpen = openDays[0];
  return `${DAY_NAMES[firstOpen]} ${hours[firstOpen]?.open_time} - ${hours[firstOpen]?.close_time}`;
};

const StoreProfile = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();

  const [store, setStore] = useState(null);
  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [reviews, setReviews] = useState({ items: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('products');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [userLocation, setUserLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSchedule, setShowSchedule] = useState(null); // branch id
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [addingToCart, setAddingToCart] = useState(null); // product id
  const shareRef = useRef(null);

  const customerToken = localStorage.getItem('customer_token');

  useEffect(() => {
    window.scrollTo(0, 0);
    getUserLocation();
  }, []);

  useEffect(() => {
    if (storeId) {
      loadStore();
    }
  }, [storeId, userLocation]);

  // Close share menu on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (shareRef.current && !shareRef.current.contains(e.target)) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        () => {}
      );
    }
  };

  const loadStore = async () => {
    try {
      setLoading(true);
      let url = `/marketplace/stores/${storeId}`;
      if (userLocation) {
        url += `?lat=${userLocation.lat}&lng=${userLocation.lng}`;
      }

      const response = await api.get(url);
      setStore(response.data.store);
      setBranches(response.data.branches || []);
      setProducts(response.data.products || []);
      setCategories(response.data.categories || []);
      setReviews(response.data.reviews || { items: [], stats: {} });
    } catch (error) {
      console.error('Error loading store:', error);
      toast.error('Error al cargar la tienda');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = (selectedCategory === 'all'
    ? products
    : products.filter(p => p.category_id === selectedCategory)
  ).filter(p => !searchQuery || p.name?.toLowerCase().includes(searchQuery.toLowerCase()));

  const handleQuickAddToCart = async (e, product) => {
    e.preventDefault();
    e.stopPropagation();

    if (!customerToken) {
      navigate(`/auth?redirect=/tienda/${storeId}`);
      return;
    }

    const branchId = branches[0]?.id;
    if (!branchId) {
      toast.error('No se encontró una sucursal disponible');
      return;
    }

    try {
      setAddingToCart(product.id);
      await api.post(`/marketplace/cart/add?customer_token=${customerToken}`, {
        product_id: product.id,
        business_id: store.id || storeId,
        branch_id: branchId,
        quantity: 1
      });
      toast.success('Agregado al carrito');
      window.dispatchEvent(new Event('cart-updated'));
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al agregar al carrito'));
    } finally {
      setAddingToCart(null);
    }
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = store ? `Mira esta tienda: ${store.name} en TodoEnLocal` : '';

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
      toast.success('Enlace copiado');
    } catch {
      toast.error('No se pudo copiar');
    }
    setShowShareMenu(false);
  };

  const handleShareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`, '_blank');
    setShowShareMenu(false);
  };

  const whatsappNumber = store?.phone?.replace(/\D/g, '') || branches[0]?.phone?.replace(/\D/g, '');

  // Loading skeleton
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <MarketplaceHeader />
        {/* Cover skeleton */}
        <div className="h-32 md:h-44 bg-slate-200 animate-pulse" />
        <div className="max-w-7xl mx-auto px-4 w-full">
          {/* Store info skeleton */}
          <div className="bg-white rounded-xl -mt-10 relative z-10 p-6 shadow-sm animate-pulse">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-20 h-20 rounded-2xl bg-slate-200" />
              <div className="flex-1 space-y-3">
                <div className="h-6 bg-slate-200 rounded w-48" />
                <div className="h-4 bg-slate-200 rounded w-32" />
                <div className="flex gap-3">
                  <div className="h-8 bg-slate-200 rounded-full w-32" />
                  <div className="h-8 bg-slate-200 rounded-full w-36" />
                </div>
              </div>
            </div>
          </div>
          {/* Tabs skeleton */}
          <div className="flex gap-6 mt-6 mb-6">
            <div className="h-10 bg-slate-200 rounded w-28 animate-pulse" />
            <div className="h-10 bg-slate-200 rounded w-28 animate-pulse" />
            <div className="h-10 bg-slate-200 rounded w-28 animate-pulse" />
          </div>
          {/* Products skeleton */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
            {[1,2,3,4,5,6,7,8].map(i => (
              <div key={i} className="bg-white rounded-xl overflow-hidden animate-pulse">
                <div className="aspect-square bg-slate-200" />
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-slate-200 rounded w-3/4" />
                  <div className="h-5 bg-slate-200 rounded w-20" />
                  <div className="h-3 bg-slate-200 rounded w-16" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <MarketplaceHeader />
        <div className="flex-1 flex flex-col items-center justify-center px-4">
          <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mb-4">
            <Store className="w-10 h-10 text-slate-300" />
          </div>
          <h2 className="text-xl font-semibold text-slate-700 mb-2">Tienda no encontrada</h2>
          <p className="text-slate-500 mb-6 text-center">Es posible que haya sido eliminada o el enlace sea incorrecto</p>
          <Link
            to="/buscar"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-medium"
            style={{ backgroundColor: COLORS.navyBlue }}
          >
            <Search className="w-4 h-4" />
            Buscar tiendas
          </Link>
        </div>
        <MarketplaceFooter />
        <MobileBottomNav />
      </div>
    );
  }

  const currentDay = getCurrentDayKey();

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <MarketplaceHeader />

      {/* Cover Banner */}
      <div
        className="h-32 md:h-44 relative"
        style={{ background: `linear-gradient(135deg, ${COLORS.navyBlue} 0%, ${COLORS.orange}88 100%)` }}
      >
        {store.cover_url && (
          <img src={store.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
      </div>

      {/* Store Info Card - overlapping banner */}
      <div className="max-w-7xl mx-auto px-4 w-full">
        <div className="bg-white rounded-xl -mt-10 relative z-10 p-5 md:p-6 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-start gap-5">
            {/* Logo */}
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-slate-100 flex items-center justify-center flex-shrink-0 overflow-hidden border-4 border-white shadow-md -mt-12 md:-mt-14">
              {store.logo_url ? (
                <img src={store.logo_url} alt={store.name} className="w-full h-full object-cover" />
              ) : (
                <Store className="w-10 h-10 text-slate-400" />
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h1 className="text-xl md:text-2xl font-bold text-slate-800 truncate">{store.name}</h1>
                    {store.verified && <VerifiedBadge size="md" />}
                  </div>
                  <p className="text-sm text-slate-500">
                    {store.business_type === 'darkstore' ? 'DarkStore' : 'Tienda Física'}
                  </p>
                </div>

                {/* Share button */}
                <div className="relative flex-shrink-0" ref={shareRef}>
                  <button
                    onClick={() => setShowShareMenu(!showShareMenu)}
                    className="p-2 rounded-full hover:bg-slate-100 transition-colors"
                    title="Compartir"
                  >
                    <Share2 className="w-5 h-5 text-slate-500" />
                  </button>
                  {showShareMenu && (
                    <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-lg border py-2 w-52 z-50">
                      <button
                        onClick={handleShareWhatsApp}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-sm text-slate-700"
                      >
                        <MessageCircle className="w-4 h-4 text-green-600" />
                        Compartir por WhatsApp
                      </button>
                      <button
                        onClick={handleCopyLink}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-sm text-slate-700"
                      >
                        {copiedLink ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4 text-slate-400" />}
                        {copiedLink ? 'Copiado' : 'Copiar enlace'}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Stats row */}
              <div className="flex flex-wrap items-center gap-3 mt-3">
                {store.average_rating > 0 && (
                  <div className="flex items-center gap-1 text-sm">
                    <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    <span className="font-semibold text-slate-800">{store.average_rating.toFixed(1)}</span>
                    <span className="text-slate-400">({store.total_reviews})</span>
                  </div>
                )}
                {store.total_orders > 0 && (
                  <span className="text-sm text-slate-500">+{store.total_orders} pedidos</span>
                )}
                {store.badges?.map(badge => (
                  <span key={badge} className="px-2.5 py-0.5 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
                    {badge === 'top_rated' && 'Mejor calificado'}
                    {badge === 'top_seller' && 'Top ventas'}
                    {badge === 'fast_response' && 'Respuesta rápida'}
                  </span>
                ))}
              </div>

              {/* Delivery options + Contact */}
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {store.pickup_enabled && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-orange-50 text-orange-700 rounded-full text-xs font-medium">
                    <Store className="w-3.5 h-3.5" /> Recoger
                  </span>
                )}
                {store.delivery_enabled && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium">
                    <Truck className="w-3.5 h-3.5" /> Envío
                  </span>
                )}
                {store.shipping_enabled && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                    <Package className="w-3.5 h-3.5" /> Paquetería
                  </span>
                )}
                <span className="mx-1 hidden md:inline text-slate-300">|</span>
                {store.phone && (
                  <a href={`tel:${store.phone}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-orange-600 transition-colors">
                    <Phone className="w-3.5 h-3.5" /> {store.phone}
                  </a>
                )}
                {store.email && (
                  <a href={`mailto:${store.email}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-orange-600 transition-colors">
                    <Mail className="w-3.5 h-3.5" /> {store.email}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b sticky top-14 z-40 mt-4">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto">
            {[
              { id: 'products', label: `Productos (${products.length})` },
              { id: 'branches', label: `Sucursales (${branches.length})` },
              { id: 'reviews', label: `Reseñas (${reviews.stats?.total || 0})` }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-3.5 px-4 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-white border-transparent'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
                style={activeTab === tab.id ? { color: COLORS.navyBlue, borderColor: COLORS.navyBlue } : {}}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-6 flex-1 w-full">
        {/* Products Tab */}
        {activeTab === 'products' && (
          <div>
            {/* Search + Category Filter */}
            <div className="mb-6 space-y-3">
              {/* Product search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Buscar productos en esta tienda..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
                />
              </div>

              {/* Category chips */}
              {categories.length > 0 && (
                <div className="flex gap-2 overflow-x-auto pb-1">
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                      selectedCategory === 'all'
                        ? 'text-white'
                        : 'bg-white text-slate-600 hover:bg-slate-100'
                    }`}
                    style={selectedCategory === 'all' ? { backgroundColor: COLORS.navyBlue } : {}}
                  >
                    Todos
                  </button>
                  {categories.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                        selectedCategory === cat.id
                          ? 'text-white'
                          : 'bg-white text-slate-600 hover:bg-slate-100'
                      }`}
                      style={selectedCategory === cat.id ? { backgroundColor: COLORS.navyBlue } : {}}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredProducts.map(product => {
                const comparePrice = product.compare_at_price;
                const retailPrice = product.price || 0;
                const hasDiscount = comparePrice && comparePrice > retailPrice;
                const discountPct = hasDiscount ? Math.round((1 - retailPrice / comparePrice) * 100) : 0;

                return (
                  <Link
                    key={product.id}
                    to={`/producto/${product.id}`}
                    className="bg-white rounded-xl overflow-hidden hover:shadow-lg transition-all group relative"
                  >
                    <div className="aspect-square bg-slate-50 relative overflow-hidden">
                      <img
                        src={getProductImage(product.image_url || product.thumbnail_url)}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => { e.target.src = getProductImage(); }}
                      />
                      {/* Discount badge */}
                      {hasDiscount && (
                        <span className="absolute top-2 left-2 px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
                          -{discountPct}%
                        </span>
                      )}
                      {/* Quick add to cart */}
                      <button
                        onClick={(e) => handleQuickAddToCart(e, product)}
                        disabled={addingToCart === product.id}
                        className="absolute bottom-2 right-2 p-2 bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-all hover:scale-110 disabled:opacity-50"
                        title="Agregar al carrito"
                      >
                        {addingToCart === product.id ? (
                          <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4" style={{ color: COLORS.orange }} />
                        )}
                      </button>
                    </div>
                    <div className="p-3">
                      <h3 className="font-medium text-slate-800 line-clamp-2 text-sm">{product.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-lg font-bold" style={{ color: COLORS.navyBlue }}>
                          ${retailPrice.toFixed(2)}
                        </span>
                        {hasDiscount && (
                          <span className="text-sm text-slate-400 line-through">${comparePrice.toFixed(2)}</span>
                        )}
                      </div>
                      {/* Delivery badges */}
                      <div className="flex gap-1.5 mt-1.5">
                        {store.delivery_enabled && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-emerald-50 text-emerald-600 rounded font-medium">Envío</span>
                        )}
                        {store.pickup_enabled && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-orange-50 text-orange-600 rounded font-medium">Recoge</span>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {filteredProducts.length === 0 && (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">
                  {searchQuery ? 'No se encontraron productos con esa búsqueda' : 'No hay productos en esta categoría'}
                </p>
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} className="mt-2 text-sm font-medium" style={{ color: COLORS.orange }}>
                    Limpiar búsqueda
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Branches Tab */}
        {activeTab === 'branches' && (
          <div className="grid md:grid-cols-2 gap-4">
            {branches.map(branch => {
              const openStatus = getBranchOpenStatus(branch);
              const isScheduleOpen = showSchedule === branch.id;

              return (
                <div
                  key={branch.id}
                  className="bg-white rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <div
                    className="p-4 cursor-pointer"
                    onClick={() => navigate(`/tienda/${storeId}/sucursal/${branch.id}`)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${openStatus.is_open ? 'bg-emerald-50' : 'bg-red-50'}`}>
                          <MapPin className={`w-5 h-5 ${openStatus.is_open ? 'text-emerald-600' : 'text-red-600'}`} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-800">{branch.name}</h3>
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                            openStatus.is_open
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${openStatus.is_open ? 'bg-emerald-500' : 'bg-red-500'}`} />
                            {openStatus.message}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>

                    <p className="text-slate-600 text-sm flex items-start gap-2 mb-3">
                      <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5 text-slate-400" />
                      {branch.address}
                    </p>

                    {/* Business Hours - Clickable summary */}
                    {branch.business_hours && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowSchedule(isScheduleOpen ? null : branch.id);
                        }}
                        className="flex items-center gap-2 text-sm text-slate-500 mb-3 hover:text-slate-700 transition-colors w-full text-left"
                      >
                        <Clock className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="flex-1">{getHoursSummary(branch.business_hours)}</span>
                        {isScheduleOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    )}

                    {/* Expanded schedule */}
                    {isScheduleOpen && branch.business_hours && (
                      <div className="mb-3 ml-6 bg-slate-50 rounded-lg p-3 space-y-1.5" onClick={(e) => e.stopPropagation()}>
                        {DAY_ORDER.map(day => {
                          const schedule = branch.business_hours[day];
                          const isToday = day === currentDay;
                          return (
                            <div
                              key={day}
                              className={`flex justify-between text-sm px-2 py-1 rounded ${isToday ? 'bg-orange-50 font-medium' : ''}`}
                            >
                              <span className={isToday ? 'text-orange-700' : 'text-slate-600'}>
                                {DAY_NAMES[day]} {isToday && '(Hoy)'}
                              </span>
                              <span className={schedule?.is_open ? (isToday ? 'text-orange-700' : 'text-slate-700') : 'text-red-400'}>
                                {schedule?.is_open ? `${schedule.open_time} - ${schedule.close_time}` : 'Cerrado'}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Service badges */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {branch.pickup_enabled && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-orange-50 text-orange-700 rounded-full">
                          <Store className="w-3 h-3" /> Recolección
                        </span>
                      )}
                      {branch.delivery_enabled && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-emerald-50 text-emerald-700 rounded-full">
                          <Truck className="w-3 h-3" />
                          Delivery {branch.delivery_radius_km ? `(${branch.delivery_radius_km} km)` : ''}
                        </span>
                      )}
                    </div>

                    {/* Delivery fee & distance */}
                    <div className="flex items-center justify-between text-sm">
                      {branch.delivery_enabled && branch.delivery_fee > 0 && (
                        <span className="text-slate-500">
                          Envío: ${branch.delivery_fee.toFixed(2)}
                        </span>
                      )}
                      {branch.delivery_enabled && branch.delivery_fee === 0 && (
                        <span className="text-emerald-600 font-medium">
                          Envío gratis
                        </span>
                      )}
                      {branch.distance_km !== undefined && branch.distance_km !== null && (
                        <span className="text-slate-400">
                          A {branch.distance_km} km de ti
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Phone */}
                  {branch.phone && (
                    <div className="px-4 py-3 border-t border-slate-100 bg-slate-50">
                      <a
                        href={`tel:${branch.phone}`}
                        className="text-sm flex items-center gap-2 transition-colors"
                        style={{ color: COLORS.navyBlue }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Phone className="w-4 h-4" /> {branch.phone}
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div>
            {/* Stats */}
            {reviews.stats?.total > 0 && (
              <div className="bg-white rounded-xl p-6 mb-6">
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-4xl font-bold" style={{ color: COLORS.navyBlue }}>{reviews.stats.average?.toFixed(1)}</p>
                    <div className="flex justify-center mt-1">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} className={`w-5 h-5 ${i <= reviews.stats.average ? 'text-yellow-400 fill-yellow-400' : 'text-slate-200'}`} />
                      ))}
                    </div>
                    <p className="text-sm text-slate-500 mt-1">{reviews.stats.total} reseñas</p>
                  </div>
                  <div className="flex-1">
                    {[5,4,3,2,1].map(rating => (
                      <div key={rating} className="flex items-center gap-2 mb-1">
                        <span className="text-sm text-slate-600 w-3">{rating}</span>
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-yellow-400 rounded-full"
                            style={{ width: `${(reviews.stats.distribution?.[rating] || 0) / reviews.stats.total * 100}%` }}
                          />
                        </div>
                        <span className="text-sm text-slate-400 w-8">{reviews.stats.distribution?.[rating] || 0}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Reviews List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {reviews.items.map(review => (
                <div key={review.id} className="bg-white rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium" style={{ backgroundColor: COLORS.navyBlue }}>
                        {review.customer_name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{review.customer_name}</p>
                        <div className="flex items-center gap-1">
                          {[1,2,3,4,5].map(i => (
                            <Star key={i} className={`w-3 h-3 ${i <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-200'}`} />
                          ))}
                        </div>
                      </div>
                    </div>
                    {review.is_verified_purchase && (
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-medium flex items-center gap-1">
                        <Shield className="w-3 h-3" /> Verificada
                      </span>
                    )}
                  </div>
                  {review.comment && <p className="text-slate-600 text-sm">{review.comment}</p>}
                  <p className="text-xs text-slate-400 mt-2">
                    {new Date(review.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>
              ))}
            </div>

            {reviews.items.length === 0 && (
              <div className="text-center py-12">
                <Star className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Aún no hay reseñas</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* WhatsApp floating button */}
      {whatsappNumber && (
        <a
          href={`https://wa.me/${whatsappNumber.startsWith('52') ? whatsappNumber : '52' + whatsappNumber}`}
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-24 right-4 sm:bottom-24 sm:right-6 z-50 w-14 h-14 bg-green-500 rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 transition-colors hover:scale-110"
          title="Contactar por WhatsApp"
        >
          <MessageCircle className="w-7 h-7 text-white fill-white" />
        </a>
      )}

      <MarketplaceFooter />
      <FloatingCartButton />
      <MobileBottomNav />
    </div>
  );
};

export default StoreProfile;
